import React, { useContext, useState } from "react";
import {
	FaQuestion,
	FaRegComment,
	FaCodeBranch,
	FaSitemap,
	FaPen,
	FaTerminal,
	FaRobot,
	FaUserNinja,
	FaToggleOn,
	FaUserAlt,
	FaClock,
	FaTrash,
	FaTasks,
	FaDollarSign,
	FaPlus,
	FaTag,
	FaEdit,
	FaComment,
	FaRandom,
	FaTicketAlt,
	FaSearch,
	FaTimes,
} from "react-icons/fa";
import { TiFlowMerge } from "react-icons/ti";
import { toast } from "react-toastify";

import "./palette.css";
import "./variables-drawer.css";

import { serverApi } from "../../services/api";

import { AccountContext } from "../../context/Account";
import { defaultVariables } from "../../helpers/default-variables";
import { EditVariable } from "../Elements/EditVariable";
import { handlePropertyVar } from "../../helpers/handle-var-property";

const Sidebar = ({ children, ...data }) => {
	const { account } = useContext(AccountContext);

	const [choseVariable, setChoseVariable] = useState("");
	const [paletteQuery, setPaletteQuery] = useState("");
	const [varsOpen, setVarsOpen] = useState(false);
	const [varQuery, setVarQuery] = useState("");

	const onDragStart = (event, nodeType) => {
		event.dataTransfer.setData("application/reactflow", nodeType);
		event.dataTransfer.effectAllowed = "move";
	};

	// Paleta agrupada por função (1a). `eloz: true` = só para contas elozAccount.
	const paletteGroups = [
		{
			label: "Mensagens",
			items: [
				{ type: "message", label: "Enviar mensagem", icon: <FaRegComment /> },
				{ type: "question", label: "Perguntar", icon: <FaQuestion /> },
				{ type: "wppFlows", label: "Whatsapp Flows", icon: <TiFlowMerge /> },
			],
		},
		{
			label: "Fluxo",
			items: [
				{ type: "sleep", label: "Delay", icon: <FaClock /> },
				{ type: "conditional", label: "Condicional", icon: <FaCodeBranch /> },
				{ type: "jump", label: "Keyword Jump", icon: <FaSitemap /> },
				{
					type: "outputbalancer",
					label: "Balanceador de saídas",
					icon: <FaRandom />,
				},
				{ type: "end", label: "Encerrar conversa", icon: <FaToggleOn /> },
				{ type: "gush", label: "Transferir para atendente", icon: <FaUserNinja /> },
			],
		},
		{
			label: "Dados",
			items: [
				{ type: "setvar", label: "Setar valor de variável", icon: <FaPen /> },
				{ type: "settag", label: "Definir tags na conversa", icon: <FaTag /> },
				{
					type: "createupdatecontact",
					label: "Criar/Atualizar contato",
					icon: <FaUserAlt />,
					eloz: true,
				},
				{ type: "createnote", label: "Adicionar anotação", icon: <FaComment /> },
			],
		},
		{
			label: "Integrações",
			items: [
				{ type: "webhook", label: "Webhook", icon: <FaTerminal /> },
				{ type: "aiagent", label: "Agente de IA", icon: <FaRobot /> },
				{ type: "createtask", label: "Criar tarefa", icon: <FaTasks />, eloz: true },
				{
					type: "createdeal",
					label: account.isGov ? "Criar solicitação" : "Criar negócio",
					icon: <FaDollarSign />,
					eloz: true,
				},
				{
					type: "createticket",
					label: "Criar ticket",
					icon: <FaTicketAlt />,
					eloz: true,
				},
			],
		},
	];

	const paletteQ = paletteQuery.trim().toLowerCase();
	const filteredGroups = paletteGroups
		.map((group) => ({
			...group,
			items: group.items.filter(
				(it) =>
					(!it.eloz || account.elozAccount) &&
					it.label.toLowerCase().includes(paletteQ)
			),
		}))
		.filter((group) => group.items.length);

	const handleDeleteVariable = async (variable) => {
		const name = variable.name;

		const isVarUsed = data.nodes.some((node) => {
			if (node?.data?.questionVar === name) return true;
			if (
				node?.data?.webhookSaveVars?.length &&
				node?.data.webhookSaveVars.find((a) => a.varBot === name)
			)
				return true;
			if (node?.data.question?.includes(`{{${name}}}`)) return true;
			if (node?.data?.msgMessage?.includes(`{{${name}}}`)) return true;
			if (node?.data.jumpVar === name) return true;
			if (node?.data?.condValue?.includes(`{{${name}}}`)) return true;
			if (node?.data?.webhookUrl?.includes(`{{${name}}}`)) return true;
			if (node?.data?.webhookBody?.includes(`{{${name}}}`)) return true;
			return false;
		});

		if (isVarUsed) {
			toast.warn("Variável sendo utilizada.");
			return;
		}

		const willDelete = window.confirm(
			"Deseja realmente excluir esta variável?"
		);
		if (!willDelete) return;
		try {
			await serverApi.post(`/bot/variable/delete/${data.botId}`, {
				name,
			});
			data.getVars();
		} catch (error) {
			alert(error.response.data.message);
		}
	};

	return (
		<>
			<EditVariable
				active={data.editOpen}
				data={data}
				handleEditVariable={data.handleEditVariable}
				contactFields={data.contactFields}
				variable={choseVariable}
				handleEditVar={data.handleEditVar}
			/>
			<aside>
				{children}

				<div className="palette">
					<div className="palette__header">Elementos</div>
					<div className="palette__hint">
						Arraste um elemento para o quadro.
					</div>
					<div className="palette__search">
						<FaSearch />
						<input
							value={paletteQuery}
							onChange={(e) => setPaletteQuery(e.target.value)}
							placeholder="Buscar elemento"
						/>
					</div>

					<div className="palette__groups">
						{filteredGroups.map((group) => (
							<div className="palette__group" key={group.label}>
								<div className="palette__groupLabel">{group.label}</div>
								{group.items.map((it) => (
									<div
										key={it.type}
										className="palette__item"
										onDragStart={(event) => onDragStart(event, it.type)}
										draggable
									>
										<span className="palette__itemIcon">{it.icon}</span>
										<span className="palette__itemLabel">{it.label}</span>
									</div>
								))}
							</div>
						))}
						{!filteredGroups.length && (
							<div className="palette__empty">Nenhum elemento encontrado</div>
						)}
					</div>

					<button
						type="button"
						className="palette__varsBtn"
						onClick={() => setVarsOpen(true)}
					>
						<FaPen />
						<span>Variáveis do fluxo</span>
						<span className="palette__varsCount">
							{data.variables ? data.variables.length : 0}
						</span>
					</button>
				</div>
			</aside>

			{varsOpen && (
				<div
					className="vars-drawer__backdrop"
					onClick={() => {
						setVarsOpen(false);
						setVarQuery("");
					}}
				>
					<div className="vars-drawer" onClick={(e) => e.stopPropagation()}>
						<div className="vars-drawer__header">
							<div className="vars-drawer__titleBox">
								<span className="vars-drawer__title">Variáveis do fluxo</span>
								<span className="vars-drawer__subtitle">
									{data.variables ? data.variables.length : 0} variáveis · use{" "}
									<code>{"{{nome}}"}</code> em qualquer mensagem
								</span>
							</div>
							<button
								type="button"
								className="vars-drawer__close"
								title="Fechar"
								onClick={() => {
									setVarsOpen(false);
									setVarQuery("");
								}}
							>
								<FaTimes />
							</button>
						</div>

						<div className="vars-drawer__tools">
							<div className="vars-drawer__search">
								<FaSearch />
								<input
									value={varQuery}
									onChange={(e) => setVarQuery(e.target.value)}
									placeholder="Buscar variável"
								/>
							</div>
							<button
								type="button"
								className="vars-drawer__new"
								onClick={data.handleModalVariable}
							>
								<FaPlus />
								<span>Nova</span>
							</button>
						</div>

						<div className="vars-drawer__list">
							{data.variables &&
								data.variables
									.filter((it) =>
										it.name
											.toLowerCase()
											.includes(varQuery.trim().toLowerCase())
									)
									.map((it) => (
										<div className="vars-drawer__item" key={it._id}>
											<div
												className={`vars-drawer__name ${
													defaultVariables.includes(it.name)
														? "is-default"
														: ""
												}`}
											>
												{it.name}{" "}
												{it.propertyAssociate
													? ` (${handlePropertyVar(it, data.contactFields)})`
													: ""}
											</div>
											<div className="vars-drawer__actions">
												{account.elozAccount ? (
													<button
														type="button"
														className="vars-drawer__action"
														title="Editar variável"
														onClick={() => {
															data.handleEditVariable();
															setChoseVariable(it);
														}}
													>
														<FaEdit />
													</button>
												) : null}
												{!defaultVariables.includes(it.name) && (
													<button
														type="button"
														className="vars-drawer__action vars-drawer__action--danger"
														title="Apagar variável"
														onClick={() => handleDeleteVariable(it)}
													>
														<FaTrash />
													</button>
												)}
											</div>
										</div>
									))}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default Sidebar;
